import React, { useState } from "react";
import { L as Link } from "../lib/i18n";
import { useLang } from "../contexts/LanguageContext";
import { useActiveLocale } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { api } from "../lib/api";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const { t } = useLang();
  const locale = useActiveLocale();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.forgotPassword(email.trim().toLowerCase(), locale);
      setDone(true);
    } catch (err) {
      // Backend always returns 200 — only network/parse errors land here
      setError(err?.response?.data?.detail || err?.message || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-md items-center px-4">
      <Card className="w-full border-zinc-800 bg-zinc-950 p-8 text-white" data-testid="forgot-password-card">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-500">{t.nav.login}</p>
        <h1 className="mt-2 font-display text-3xl font-bold">{t.auth.forgotTitle}</h1>
        {!done ? (
          <>
            <p className="mt-4 text-sm leading-relaxed text-zinc-400">{t.auth.forgotIntro}</p>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">{t.auth.email}</Label>
                <div className="relative mt-2">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                  <Input
                    type="email"
                    required
                    autoComplete="email"
                    inputMode="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="border-zinc-800 bg-black pl-10 text-white"
                    placeholder="dein@email.ch"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              {error && <p className="text-sm text-red-500" data-testid="forgot-error">{error}</p>}
              <Button type="submit" disabled={busy || !email.trim()} className="w-full bg-red-600 text-white hover:bg-red-500" data-testid="forgot-submit">
                {busy ? "..." : t.auth.forgotSubmit}
              </Button>
            </form>
          </>
        ) : (
          <div className="mt-6 space-y-5" data-testid="forgot-success">
            <div className="flex items-start gap-3 rounded-lg border border-green-500/30 bg-green-500/5 p-4">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-500" />
              <p className="text-sm leading-relaxed text-zinc-200">{t.auth.forgotSent}</p>
            </div>
          </div>
        )}
        <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-red-500" data-testid="back-to-login">
          <ArrowLeft className="h-3.5 w-3.5" /> {t.auth.forgotBack}
        </Link>
      </Card>
    </div>
  );
}
